import React from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Link } from 'react-router-dom';
import GalleryLifeStyle from './GalleryLifeStyle';
import Ceo from '../../assets/images/ceo.jpg';
import { motion, AnimatePresence } from 'framer-motion';
import MenuServices from './MenuServices';
import './ServicePage.css';

// Palette white / gold / glass
const colors = {
  text: '#3B2F2A',
  textSoft: '#5C4A3D',
  gold: '#C69C6D',
  goldDark: '#B88A56',
  goldLight: '#F5E1C8',
  glassBg: 'rgba(255, 255, 255, 0.7)',
  glassBorder: 'rgba(198, 156, 109, 0.25)',
  shadow: '0 8px 24px rgba(107, 90, 70, 0.06)',
  shadowHover: '0 16px 32px rgba(184, 138, 86, 0.15)',
};

// Variantes d'animation (inchangées mais adaptées)
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      duration: 0.8,
    },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 50, scale: 0.9 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { type: 'spring', stiffness: 100, damping: 15 },
  },
};

const textVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: 'easeOut' },
  },
};

const listItemVariants = {
  hidden: { opacity: 0, x: -30 },
  visible: (i) => ({
    opacity: 1,
    x: 0,
    transition: { delay: i * 0.2, duration: 0.5, ease: 'easeOut' },
  }),
  hover: {
    scale: 1.02,
    x: 15,
    color: '#B88A56',
    transition: { type: 'spring', stiffness: 400, damping: 10 },
  },
};

const ServicePage = () => {
  return (
    <>
      <style>{`
        .service-page-section {
          position: relative;
          padding: 80px 0;
          background: linear-gradient(135deg, #EAD5C0 0%, #D4B895 30%, #C69C6D 60%, #E8CFB5 100%);
          background-size: 400% 400%;
          animation: goldGradient 15s ease infinite;
          overflow: hidden;
        }

        @keyframes goldGradient {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }

        /* Orbes floues */
        .service-page-section::before,
        .service-page-section::after {
          content: '';
          position: absolute;
          border-radius: 50%;
          filter: blur(120px);
          opacity: 0.2;
          z-index: 0;
          pointer-events: none;
        }

        .service-page-section::before {
          width: 50vw;
          height: 50vw;
          background: #F5E1C8;
          top: -10%;
          left: -5%;
          animation: floatOrb 18s ease-in-out infinite;
        }

        .service-page-section::after {
          width: 40vw;
          height: 40vw;
          background: #E8CFB5;
          bottom: -10%;
          right: -5%;
          animation: floatOrb 15s ease-in-out infinite alternate-reverse;
        }

        @keyframes floatOrb {
          0% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(5%, 10%) scale(1.15); }
          100% { transform: translate(-5%, 5%) scale(0.9); }
        }

        .container {
          position: relative;
          z-index: 2;
        }

        /* Cartes et widgets en glassmorphism */
        .glass-card {
          background: ${colors.glassBg};
          backdrop-filter: blur(18px);
          -webkit-backdrop-filter: blur(18px);
          border: 1px solid ${colors.glassBorder};
          border-radius: 24px;
          padding: 25px;
          box-shadow: ${colors.shadow};
          transition: box-shadow 0.3s;
        }

        .glass-card:hover {
          box-shadow: ${colors.shadowHover};
        }

        .title-section {
          margin-bottom: 30px;
        }

        .gradient-text-main {
          font-size: 2.5rem;
          font-weight: 700;
          color: ${colors.text};
          background: linear-gradient(135deg, ${colors.text}, ${colors.goldDark});
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          margin-bottom: 10px;
        }

        .title-underline {
          width: 80px;
          height: 3px;
          background: linear-gradient(90deg, ${colors.goldDark}, ${colors.gold});
          border-radius: 2px;
        }

        .elegant-text {
          font-size: 1.1rem;
          color: ${colors.textSoft};
          line-height: 1.7;
        }

        .activities-title {
          font-size: 1.5rem;
          font-weight: 600;
          color: ${colors.text};
          margin-bottom: 20px;
        }

        .activities-list {
          list-style: none;
          padding: 0;
        }

        .activity-item {
          display: flex;
          align-items: flex-start;
          gap: 15px;
          margin-bottom: 20px;
          padding: 15px;
          background: ${colors.glassBg};
          backdrop-filter: blur(12px);
          border: 1px solid ${colors.glassBorder};
          border-radius: 16px;
          box-shadow: ${colors.shadow};
          transition: all 0.3s;
          cursor: default;
        }

        .activity-item:hover {
          background: rgba(255,255,255,0.9);
          border-color: ${colors.gold};
          box-shadow: ${colors.shadowHover};
        }

        .icon-circle {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: linear-gradient(135deg, ${colors.gold}, ${colors.goldDark});
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 1.2rem;
        }

        .activity-title {
          font-size: 1.2rem;
          font-weight: 600;
          color: ${colors.text};
          margin-bottom: 5px;
        }

        .activity-description {
          font-size: 0.95rem;
          color: ${colors.textSoft};
          margin: 0;
        }

        .call-to-action {
          background: ${colors.glassBg};
          backdrop-filter: blur(16px);
          border: 1px solid ${colors.glassBorder};
          border-radius: 24px;
          padding: 30px;
          box-shadow: ${colors.shadow};
        }

        .action-title {
          font-size: 1.5rem;
          font-weight: 600;
          color: ${colors.text};
        }

        .action-subtitle {
          color: ${colors.textSoft};
        }

        .btn-premium {
          background: linear-gradient(135deg, ${colors.gold}, ${colors.goldDark});
          color: white;
          border: none;
          padding: 0.8rem 2rem;
          border-radius: 50px;
          font-weight: 600;
          display: inline-flex;
          align-items: center;
          gap: 10px;
          text-decoration: none;
          transition: all 0.3s;
          box-shadow: 0 8px 20px rgba(198,156,109,0.3);
        }

        .btn-premium:hover {
          transform: translateY(-2px);
          box-shadow: 0 12px 28px rgba(198,156,109,0.4);
          color: white;
        }

        .btn-arrow {
          font-size: 1.2rem;
        }

        /* Sidebar */
        .sidebar-left .widget {
          margin-bottom: 30px;
        }

        .quote-border {
          border-left: 3px solid ${colors.gold};
          padding-left: 15px;
        }

        .quote-author {
          font-size: 1rem;
          font-weight: 600;
          color: ${colors.text};
        }

        .quote-subtext {
          font-size: 0.85rem;
          color: ${colors.textSoft};
        }

        @media (max-width: 992px) {
          .service-page-section {
            padding: 60px 0;
          }
        }
      `}</style>

      <section className="service-page-section">
        <div className="container">
          <div className="row">
            <div className="col-xl-3 col-lg-4">
              <div className="sidebar sidebar-left">
                <MenuServices />

                <motion.div variants={cardVariants} initial="hidden" animate="visible">
                  <div className="quote-item quote-border glass-card">
                    <div className="quote-text-border">
                      <motion.div
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.8, duration: 0.6 }}
                      >
                        Parce que vous méritez le meilleur, simplement.
                      </motion.div>
                    </div>
                    <div className="quote-item-footer d-flex align-items-center mt-3">
                      <motion.img
                        className="testimonial-thumb rounded-circle"
                        src={Ceo}
                        alt="CEO"
                        style={{ width: 60, height: 60, objectFit: 'cover' }}
                        initial={{ opacity: 0, scale: 0 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 1, type: 'spring' }}
                      />
                      <div className="ms-3">
                        <motion.h3
                          className="quote-author mb-0"
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 1.2 }}
                        >
                          Ndagbia Mobutu
                        </motion.h3>
                        <motion.span
                          className="quote-subtext"
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 1.4 }}
                        >
                          CEO, Azyekos Services
                        </motion.span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>

            <div className="col-xl-8 col-lg-8">
              <motion.div variants={containerVariants} initial="hidden" animate="visible">
                <motion.div className="title-section" variants={cardVariants}>
                  <motion.h2
                    className="gradient-text-main"
                    initial={{ opacity: 0, y: -50 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, type: 'spring' }}
                  >
                    LifeStyle & Evasion
                  </motion.h2>
                  <motion.div
                    className="title-underline"
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: 1 }}
                    transition={{ delay: 0.5, duration: 0.8 }}
                  />
                </motion.div>

                <motion.div variants={containerVariants}>
                  <div className="col-md-12">
                    <motion.div variants={textVariants}>
                      <motion.p
                        className="elegant-text"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.6, duration: 0.8 }}
                      >
                        Azyekos vous invite à vivre une évasion unique, alliant raffinement, liberté, confort et art de vivre sur mesure. À travers une approche personnalisée et un sens aigu du détail, nous transformons chaque instant en une expérience exceptionnelle.
                      </motion.p>
                      <motion.p
                        className="elegant-text"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.8, duration: 0.8 }}
                      >
                        Notre expertise réside dans notre capacité à anticiper vos envies et à créer des moments d'exception qui vous ressemblent. En alliant l'élégance du service haut de gamme à la liberté de choix, Azyekos redéfinit l'art de vivre et de voyager.
                      </motion.p>
                    </motion.div>
                  </div>
                </motion.div>

                <div className="gap-40"></div>

                <motion.div id="page-slider" className="page-slider" variants={cardVariants}>
                  <GalleryLifeStyle />
                </motion.div>

                <div className="gap-40"></div>

                <motion.div variants={containerVariants}>
                  <div className="col-md-12">
                    <motion.h3 className="activities-title" variants={textVariants}>
                      Voici 5 activités clés du service Lifestyle / Évasion proposé par Azyekos :
                    </motion.h3>
                    <ul className="activities-list">
                      {[
                        {
                          title: 'Organisation de voyages sur mesure',
                          description: 'Destinations exclusives, hébergements luxueux, itinéraires personnalisés.',
                        },
                        {
                          title: 'Expériences culturelles privées',
                          description: 'Visites guidées, accès VIP à des événements, rencontres d’artistes.',
                        },
                        {
                          title: 'Séjours bien-être haut de gamme',
                          description: 'Spas, retraites, soins personnalisés dans des cadres d’exception.',
                        },
                        {
                          title: 'Événements lifestyle exclusifs',
                          description: 'Dîners privés, soirées thématiques, escapades romantiques.',
                        },
                        {
                          title: 'Service de conciergerie personnalisé',
                          description: 'Gestion complète de vos envies, réservations, et logistique d’évasion.',
                        },
                      ].map((activity, index) => (
                        <motion.li
                          key={index}
                          className="activity-item"
                          custom={index}
                          variants={listItemVariants}
                          whileHover="hover"
                        >
                          <div className="activity-icon">
                            <motion.div className="icon-circle" whileHover={{ rotate: 360 }} transition={{ duration: 0.6 }}>
                              {index + 1}
                            </motion.div>
                          </div>
                          <div className="activity-content">
                            <h4 className="activity-title">{activity.title}</h4>
                            <p className="activity-description">{activity.description}</p>
                          </div>
                        </motion.li>
                      ))}
                    </ul>
                  </div>
                </motion.div>

                <div className="gap-40"></div>

                <motion.div className="call-to-action" variants={cardVariants}>
                  <div className="row align-items-center">
                    <div className="col-md-8 text-center text-md-start">
                      <motion.div
                        initial={{ opacity: 0, x: -30 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 1.2 }}
                      >
                        <h4 className="action-title">Êtes-vous intéressé par ce service ?</h4>
                        <p className="action-subtitle">Laissez-nous créer l'expérience parfaite pour vous</p>
                      </motion.div>
                    </div>
                    <div className="col-md-4 text-center text-md-end mt-3 mt-md-0">
                      <motion.div
                        whileHover={{ scale: 1.05, transition: { type: 'spring', stiffness: 300 } }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <Link to="/Contact" className="btn btn-premium">
                          <span className="btn-text">Contactez-nous</span>
                          <motion.div className="btn-arrow" initial={{ x: 0 }} whileHover={{ x: 5 }} transition={{ type: 'spring', stiffness: 400 }}>
                            →
                          </motion.div>
                        </Link>
                      </motion.div>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default ServicePage;